import { useState, useMemo, useEffect, useCallback } from 'react';
import { X, FileSpreadsheet } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import { buildWorkbookBlob, downloadWorkbook } from '../utils/xlsxUtil';
import { fetchPosOrdersInRange, summarisePosSales, rankBestSellers } from '../utils/posUtil';
import { POS_TOP_PRODUCTS_LIMIT, POS_PAGE_SIZE } from '../constants/posConst';
import PosReceipt from './PosReceipt';
import PosPager from './PosPager';
import Modal from './Modal';
import '../css/Pos.css';

/**
 * The single series colour for the best-seller bars.
 *
 * ONE colour for every bar, not ten. These are products — nominal categories
 * with no natural order — measured on one axis, so a different hue per bar
 * would encode nothing the bar's own length doesn't already say, and would
 * burn the only free channel on decoration.
 *
 * This exact value was chosen by validation, not by eye: the club's own
 * --jt-cyan-deep (#5b948c) and --jt-cyan (#6fb5ab) both fall below the chroma
 * floor at bar size — they read as grey — and the lighter one also misses 3:1
 * against the card. #0e9384 is the nearest teal that passes the lightness
 * band, the chroma floor and the contrast check, so the chart still reads as
 * the club's colour while actually being legible.
 */
const BAR_COLOR = '#0e9384';

/**
 * Today as the YYYY-MM-DD a <input type="date"> expects.
 *
 * 'en-CA' is the shortest way to get that shape out of a local date without
 * hand-padding the parts. Deliberately the DEVICE's local day, because
 * fetchPosOrdersInRange turns these strings into local-midnight timestamps —
 * a day taken from one zone and bounded in another would drop the evening's
 * orders.
 *
 * @returns {string} e.g. '2026-09-09'
 */
const todayStr = () => new Date().toLocaleDateString('en-CA');

/**
 * A loaded range as a heading, collapsed to one date when both ends match.
 *
 * @param {string} from - inclusive start, YYYY-MM-DD
 * @param {string} to - inclusive end, YYYY-MM-DD
 * @returns {string} e.g. 'Sep 9, 2026' or 'Aug 10, 2026 – Sep 9, 2026'
 */
const rangeLabel = (from, to) => {
  const pretty = (value) => {
    const [y, m, d] = String(value || '').split('-').map(Number);
    if (!y || !m || !d) return value || '';
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { dateStyle: 'medium' });
  };
  return from === to ? pretty(from) : `${pretty(from)} – ${pretty(to)}`;
};

/**
 * Top-selling products as horizontal bars.
 *
 * Hand-rolled SVG rather than a charting library, matching
 * ExportSummaryChart.jsx — one chart does not justify a dependency, and this
 * way it inherits the theme directly.
 *
 * Horizontal because product names are long: ten rotated labels under
 * vertical bars is the least readable arrangement available.
 *
 * @param {{rows: Array<{name: string, quantity: number, revenue: number}>}} props
 * @returns {JSX.Element|null}
 */
const BestSellerChart = ({ rows }) => {
  const { config } = useConfig();
  // Defined per component rather than at module scope: the symbol comes from
  // Config -> System, so it needs `config` in hand. Call sites read the same
  // as they always did.
  const peso = (value) => formatMoney(value, config);

  if (!rows.length) return null;

  const max = Math.max(...rows.map(r => r.quantity), 1);
  const ROW_H = 26;          // 16px bar + 10px air — the band is never filled
  const BAR_H = 16;
  const LABEL_W = 118;
  const VALUE_W = 78;
  const WIDTH = 520;         // fixed, and capped in CSS: a viewBox alone
                             // stretches to the container and the bars grew
                             // to the width of the card.
  const RADIUS = 4;
  const height = rows.length * ROW_H;
  const track = WIDTH - LABEL_W - VALUE_W;

  /**
   * A bar rounded at its DATA END only, square against the baseline.
   *
   * A rect with rx rounds all four corners, which softens the baseline the
   * bars are measured from and makes short bars read as lozenges. This keeps
   * the left edge sharp so every bar starts from the same hard line.
   *
   * @param {number} x @param {number} y @param {number} w @param {number} h
   * @returns {string} an SVG path
   */
  const barPath = (x, y, w, h) => {
    const r = Math.min(RADIUS, w);
    return `M${x},${y} H${x + w - r} A${r},${r} 0 0 1 ${x + w},${y + r}`
      + ` V${y + h - r} A${r},${r} 0 0 1 ${x + w - r},${y + h} H${x} Z`;
  };

  return (
    <div className="pos-chart-wrap">
      <svg
        className="pos-chart"
        viewBox={`0 0 ${WIDTH} ${height}`}
        role="img"
        aria-label={`Top ${rows.length} products by units sold`}
        preserveAspectRatio="xMinYMin meet"
      >
        {rows.map((row, i) => {
          const y = i * ROW_H;
          const barWidth = Math.max(2, (row.quantity / max) * track);
          return (
            <g key={row.productId || row.name}>
              <text x={0} y={y + BAR_H - 3} className="pos-chart-label">
                {row.name.length > 16 ? `${row.name.slice(0, 15)}…` : row.name}
              </text>
              {/* A faint track so short bars are still read against a length,
                  not against empty space. */}
              <rect x={LABEL_W} y={y} width={track} height={BAR_H} rx={RADIUS} className="pos-chart-track" />
              <path d={barPath(LABEL_W, y, barWidth, BAR_H)} fill={BAR_COLOR}>
                <title>{`${row.name}: ${row.quantity} sold, ${peso(row.revenue)}`}</title>
              </path>
              <text x={LABEL_W + track + 8} y={y + BAR_H - 3} className="pos-chart-value">
                {row.quantity}
                <tspan className="pos-chart-sub"> · {peso(row.revenue)}</tspan>
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/**
 * Sales — a report over a date range, laid out like the booking export
 * screen: read the breakdown, page through the records, export to Excel.
 *
 * Opens on TODAY'S takings rather than an empty screen: "what have we sold
 * today" is the question the counter asks all shift, so it is answered on
 * arrival and picking a range is reserved for looking further back.
 *
 * @returns {JSX.Element}
 */
function PosSales() {
  const { config } = useConfig();
  // Defined per component rather than at module scope: the symbol comes from
  // Config -> System, so it needs `config` in hand. Call sites read the same
  // as they always did.
  const peso = (value) => formatMoney(value, config);
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [orders, setOrders] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);
  const [modal, setModal] = useState(null);
  // The range the rows on screen actually came from, which is not the same as
  // the two inputs: those change as soon as staff touch them, and labelling
  // the table with a range it was never fetched for would misreport the
  // figures under it.
  const [loadedRange, setLoadedRange] = useState(null);

  const summary = useMemo(() => (orders ? summarisePosSales(orders) : null), [orders]);
  const topProducts = useMemo(
    () => (orders ? rankBestSellers(orders, POS_TOP_PRODUCTS_LIMIT) : []),
    [orders],
  );

  const pageRows = orders ? orders.slice((page - 1) * POS_PAGE_SIZE, page * POS_PAGE_SIZE) : [];

  /**
   * Loads the orders for one date range into the report.
   *
   * Takes its dates as arguments rather than reading state so the same
   * function serves both callers: the mount effect below, which must run
   * against today whatever the inputs say, and the Fetch Sales button.
   *
   * @param {string} from - inclusive start, YYYY-MM-DD
   * @param {string} to - inclusive end, YYYY-MM-DD
   * @returns {Promise<void>}
   */
  const loadSales = useCallback(async (from, to) => {
    setFetching(true);
    try {
      const list = await fetchPosOrdersInRange(from, to);
      setOrders(list);
      setLoadedRange({ from, to });
      setPage(1);
    } catch (error) {
      setModal({ title: 'Could not load sales', message: error.message, type: 'error' });
    } finally {
      setFetching(false);
    }
  }, []);

  // Today's sales, once, on arrival. Pinned to todayStr() rather than to the
  // startDate/endDate state so this cannot re-run and overwrite a range staff
  // have since fetched.
  useEffect(() => {
    const today = todayStr();
    loadSales(today, today);
  }, [loadSales]);

  const handleFetch = () => loadSales(startDate, endDate);

  /**
   * Three sheets: the orders, every line item, and the best sellers.
   *
   * Line items get their own sheet rather than being crammed into the order
   * rows: one order holds several products, and a spreadsheet can only pivot
   * or sum what sits one-per-row.
   */
  const handleExport = () => {
    if (!orders?.length) return;

    const money = (value) => ({ value: Number(value) || 0, type: 'money' });
    const count = (value) => ({ value: Number(value) || 0, type: 'number' });
    const text = (value) => ({ value: value ?? '' });

    const orderSheet = {
      name: 'Orders',
      columnWidths: [26, 20, 10, 14, 14, 14, 28],
      rows: [
        { variant: 'header', cells: ['Order ID', 'Date', 'Items', 'Total', 'Cash', 'Change', 'Cashier'].map(text) },
        ...orders.map(order => ({
          cells: [
            text(order.id),
            text(new Date(order.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })),
            count(order.itemCount),
            money(order.total),
            money(order.cashReceived),
            money(order.change),
            text(order.cashier),
          ],
        })),
        {
          variant: 'footer',
          cells: [
            text('TOTAL'), text(''),
            count(orders.reduce((n, o) => n + (Number(o.itemCount) || 0), 0)),
            money(summary?.total),
            text(''), text(''), text(''),
          ],
        },
      ],
    };

    const itemRows = orders.flatMap(order => (order.items || []).map(item => ({
      cells: [
        text(order.id),
        text(new Date(order.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })),
        text(item.name),
        text(item.category || 'Uncategorised'),
        count(item.quantity),
        money(item.price),
        money(item.lineTotal),
      ],
    })));

    const itemSheet = {
      name: 'Items',
      columnWidths: [26, 20, 28, 18, 8, 12, 14],
      rows: [
        { variant: 'header', cells: ['Order ID', 'Date', 'Product', 'Category', 'Qty', 'Unit price', 'Line total'].map(text) },
        ...itemRows,
        {
          variant: 'footer',
          cells: [
            text('TOTAL'), text(''), text(''), text(''),
            count(summary?.itemCount),
            text(''),
            money(summary?.total),
          ],
        },
      ],
    };

    const bestSheet = {
      name: 'Best sellers',
      columnWidths: [28, 10, 14, 10],
      rows: [
        { variant: 'header', cells: ['Product', 'Units', 'Revenue', 'Share'].map(text) },
        ...rankBestSellers(orders, 100).map(row => ({
          cells: [
            text(row.name),
            count(row.quantity),
            money(row.revenue),
            text(summary?.total ? `${((row.revenue / summary.total) * 100).toFixed(1)}%` : '—'),
          ],
        })),
      ],
    };

    const blob = buildWorkbookBlob({ sheets: [orderSheet, itemSheet, bestSheet] });
    // Named for the range the rows CAME from, not the inputs — staff often
    // retype the dates before exporting, and a file called by the range they
    // are about to fetch would be filed under the wrong period.
    downloadWorkbook(blob, `pos-sales-${loadedRange?.from}-to-${loadedRange?.to}.xlsx`);
  };

  return (
    <div className="pos-sales">
      {/* ---------- Range ---------- */}
      <section className="pos-section">
        <div className="pos-section-head">
          <h2>Sales Reports</h2>
          {/* Names the range the figures below actually cover, so "today" on
              first load and the chosen period afterwards read the same way —
              a fixed "showing today's sales" line would go quietly wrong the
              moment staff fetched last week. */}
          <p>
            {loadedRange
              ? <>Showing sales for <strong>{rangeLabel(loadedRange.from, loadedRange.to)}</strong>. Select a date range to report on another period.</>
              : 'Generate a sales report by selecting a date range.'}
          </p>
        </div>

        <div className="pos-range">
          <label className="pos-field">
            <span>Start Date</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label className="pos-field">
            <span>End Date</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
          <button type="button" className="pos-btn pos-btn-primary pos-fetch" onClick={handleFetch} disabled={fetching}>
            {fetching ? 'Fetching…' : 'Fetch Sales'}
          </button>
        </div>
      </section>

      {fetching && <div className="pos-loading">{spinnerRing('Loading sales…')}</div>}

      {!fetching && orders && orders.length === 0 && (
        <section className="pos-section">
          <p className="pos-empty-line">
            No orders for {loadedRange ? rangeLabel(loadedRange.from, loadedRange.to) : 'this range'}.
          </p>
        </section>
      )}

      {!fetching && orders && orders.length > 0 && (
        <>
          {/* ---------- Chart + breakdown ---------- */}
          <section className="pos-section">
            {/* No legend: one series, and every bar is already named on its
                own row — the legend was restating the chart. */}
            <div className="pos-summary-head">
              <span className="pos-summary-total">{peso(summary.total)}</span>
              <span className="pos-summary-sub">
                {summary.orderCount} order{summary.orderCount === 1 ? '' : 's'} &middot; {summary.itemCount} items sold
              </span>
            </div>

            <h3 className="pos-chart-title">Top {POS_TOP_PRODUCTS_LIMIT} products by units sold</h3>
            <BestSellerChart rows={topProducts} />

            <table className="pos-breakdown">
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col">Product</th>
                  <th scope="col" className="num">Units</th>
                  <th scope="col" className="num">Amount</th>
                  <th scope="col" className="num">Share</th>
                </tr>
              </thead>
              <tbody>
                {summary.groups.map((group) => group.products.map((product, pi) => (
                  <tr key={`${group.name}-${product.name}`}>
                    {/* The category cell spans its products, so the table reads
                        as groups rather than as a repeated column. */}
                    {pi === 0 && (
                      <th scope="row" rowSpan={group.products.length} className="pos-breakdown-group">
                        {group.name}
                        <small>{peso(group.revenue)}</small>
                      </th>
                    )}
                    <td>{product.name}</td>
                    <td className="num">{product.quantity}</td>
                    <td className="num">{peso(product.revenue)}</td>
                    <td className="num">{product.share < 1 ? '<1%' : `${Math.round(product.share)}%`}</td>
                  </tr>
                )))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" colSpan={2}>Total</th>
                  <td className="num">{summary.itemCount}</td>
                  <td className="num">{peso(summary.total)}</td>
                  <td className="num">100%</td>
                </tr>
              </tfoot>
            </table>
          </section>

          {/* ---------- Records ---------- */}
          <section className="pos-section">
            <div className="pos-section-head pos-section-head-row">
              <div>
                <h2>Preview ({orders.length} order{orders.length === 1 ? '' : 's'})</h2>
                <p>Tap a row to see its receipt.</p>
              </div>
            </div>

            <div className="pos-table-scroll">
              <table className="pos-records">
                <thead>
                  <tr>
                    <th scope="col">Order ID</th>
                    <th scope="col">Date</th>
                    <th scope="col">Items</th>
                    <th scope="col" className="num">Qty</th>
                    <th scope="col" className="num">Total</th>
                    <th scope="col">Cashier</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((order) => (
                    <tr key={order.id} onClick={() => setViewing(order)} tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') setViewing(order); }}>
                      <td className="pos-records-id">{order.id}</td>
                      <td>{new Date(order.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                      <td>{(order.items || []).map(i => `${i.quantity}x ${i.name}`).join(', ')}</td>
                      <td className="num">{order.itemCount}</td>
                      <td className="num">{peso(order.total)}</td>
                      <td>{order.cashier}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row" colSpan={4}>TOTAL</th>
                    <td className="num">{peso(summary.total)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>

            <PosPager page={page} totalItems={orders.length} onPageChange={setPage} />
          </section>

          <button type="button" className="pos-btn pos-btn-primary pos-export" onClick={handleExport}>
            <FileSpreadsheet size={16} /> Export as Excel (.xlsx)
          </button>
        </>
      )}

      {viewing && (
        <div className="pos-modal-overlay">
          <div className="pos-modal pos-modal-receipt" role="dialog" aria-modal="true" aria-label="Order receipt">
            <header className="pos-modal-head">
              <h3>Receipt</h3>
              <button type="button" className="pos-modal-close" onClick={() => setViewing(null)} aria-label="Close"><X size={18} /></button>
            </header>
            <div className="pos-modal-body">
              {/* No payment note — this order is history, and the instruction
                  only makes sense before money changes hands. */}
              <PosReceipt order={viewing} courtName={config?.courtName} reference={viewing.id} />
            </div>
            <footer className="pos-modal-foot">
              <span className="pos-foot-spacer" />
              <button type="button" className="pos-btn pos-btn-primary" onClick={() => setViewing(null)}>Close</button>
            </footer>
          </div>
        </div>
      )}

      <Modal
        isOpen={!!modal}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
        confirmText="OK"
        onClose={() => setModal(null)}
      />
    </div>
  );
}

export default PosSales;
